<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :draggable="false"
    :style="{ width: '520px' }"
    :header="dialogHeader"
  >
    <div class="spm-gdpr-erase">
      <Message
        severity="error"
        :closable="false"
      >
        {{ t('myLists.actions.gdprErase.warningIrreversible') }}
      </Message>

      <p class="spm-gdpr-erase__line">
        {{ t('myLists.actions.gdprErase.summary') }}
      </p>

      <p class="spm-gdpr-erase__line">
        {{ t('myLists.actions.gdprErase.noResync') }}
      </p>

      <div>
        <label
          for="gdpr-erase-confirmation"
          class="spm-gdpr-erase__label"
        >
          {{ t('myLists.actions.gdprErase.confirmLabel', { word: confirmWord }) }}
        </label>
        <InputText
          id="gdpr-erase-confirmation"
          v-model="confirmation"
          class="w-full"
          autocomplete="off"
          :placeholder="confirmWord"
          :class="{ 'p-invalid': confirmation && !isMatch }"
          @keyup.enter="isMatch && confirm()"
        />
      </div>
    </div>

    <template #footer>
      <SpmButton
        :label="t('common.close')"
        icon="pi pi-times"
        class-style="p-button p-button-secondary"
        @click="close"
      />
      <SpmButton
        :label="t('myLists.actions.gdprErase.confirmButton')"
        icon="pi pi-trash"
        class-style="p-button p-button-danger"
        :disabled="!isMatch"
        @click="confirm"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import type { ActionPayload, GdprEraseResult } from './types';

/**
 * Effacement RGPD d'un ou plusieurs contacts (SC-3851).
 *
 * Action irréversible : la confirmation se fait en tapant un mot, sur le modèle
 * de CustomKpiDeleteDialog. Le mot saisi est transmis au parent puis à l'API, qui
 * le revérifie — un appel direct ne doit pas pouvoir court-circuiter cette étape.
 */
export default defineComponent({
  name: 'GdprEraseAction',

  components: {
    Dialog,
    InputText,
    Message,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t, locale } = useI18n();

    const isVisible = ref(false);
    const confirmation = ref('');
    const currentPayload = ref<ActionPayload | null>(null);

    // Mot attendu selon la langue de l'interface. Le back accepte les deux.
    const confirmWord = computed(() => (locale.value?.startsWith('fr') ? 'effacer' : 'erase'));

    const isMatch = computed(
      () => confirmation.value.trim().toLowerCase() === confirmWord.value,
    );

    // Un seul en-tête : l'effacement ne porte jamais que sur un contact.
    const dialogHeader = computed(() => t('myLists.actions.gdprErase.headerRow'));

    const open = (payload: ActionPayload) => {
      currentPayload.value = payload;
      confirmation.value = '';
      isVisible.value = true;
    };

    const close = () => {
      isVisible.value = false;
      confirmation.value = '';
      currentPayload.value = null;
    };

    const confirm = () => {
      if (!currentPayload.value || !isMatch.value) return;

      const result: GdprEraseResult = {
        context: currentPayload.value.context,
        confirmation: confirmation.value.trim().toLowerCase(),
        selectionInfo: currentPayload.value.selectionInfo,
        rowId: currentPayload.value.rowId,
      };

      emit('confirm', result);
      close();
    };

    expose({ open });

    return {
      t,
      isVisible,
      confirmation,
      confirmWord,
      isMatch,
      dialogHeader,
      close,
      confirm,
    };
  },
});
</script>

<style scoped lang="scss">
.spm-gdpr-erase {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__line {
    margin: 0;
  }

  &__label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 0.25rem;
  }
}
</style>
